import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { journeyApi } from '../../api/journeyApi';
import { journeyChecklistApi } from '../../api/journeyChecklistApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { Alert, Button, EmptyState, ErrorState, LoadingState, TextField } from '../../components/ui';
import useMemberDisplayNames from '../member-display/useMemberDisplayNames';
import { memberDisplayName } from '../member-display/memberDisplayModel';
import {
  fillWorkspaceProductChrome,
  WORKSPACE_PRODUCT_CHROME,
} from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import {
  buildChecklistCreatePayload,
  buildChecklistReassignPayload,
  checklistAssigneeErrorMessage,
  checklistReassignSelectValue,
  classifyLogisticsError,
  deriveChecklistProgress,
  eligibleChecklistAssignees,
  filterChecklistItems,
  normalizeChecklistBoard,
  resolveChecklistAssigneePresentation,
  validateChecklistCreateDraft,
} from './checklistModel';
import '../journey-workspace/journey-workspace.css';
import '../journey-workspace/journey-logistics.css';
import './journey-checklist.css';

const WRITE_ROLES = new Set(['OWNER', 'COORDINATOR', 'MEMBER']);
const REMOVE_ROLES = new Set(['OWNER', 'COORDINATOR']);
const WRITABLE_LIFECYCLES = new Set(['DRAFT', 'READY', 'TRAVELING']);

export default function JourneyChecklistPage() {
  const { journeyId } = useParams();
  const { t } = useTravelTranslation();
  const { user } = useAuth();
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef(false);

  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [refreshErrorModel, setRefreshErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [board, setBoard] = useState({ checklistId: null, items: [] });
  const [busy, setBusy] = useState(false);
  const [busyItemId, setBusyItemId] = useState(null);
  const [formError, setFormError] = useState('');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftAssignee, setDraftAssignee] = useState('');
  const [filter, setFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const titleId = useId();
  const assigneeId = useId();

  const memberIdsForDisplay = useMemo(() => {
    const ids = [];
    (journey?.members || []).forEach((member) => ids.push(member.memberId));
    (board.items || []).forEach((item) => {
      if (item.assignedMemberId != null) ids.push(item.assignedMemberId);
    });
    return ids;
  }, [journey?.members, board.items]);
  const memberSummaries = useMemberDisplayNames(memberIdsForDisplay);
  const eligibleAssignees = useMemo(
    () => eligibleChecklistAssignees(journey?.members),
    [journey?.members],
  );

  const applyBoard = useCallback((payload) => {
    setBoard(normalizeChecklistBoard(payload));
  }, []);

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    if (!soft) {
      setPhase('loading');
      setErrorModel(null);
      setRefreshErrorModel(null);
    }
    setFormError('');
    setConfirmDelete(null);
    try {
      const [detailPayload, boardPayload] = await Promise.all([
        journeyApi.getDetail(journeyId),
        journeyChecklistApi.getBoard(journeyId),
      ]);
      if (requestId !== generation.current) return;
      setJourney(normalizeJourneyWorkspace(detailPayload, viewerMemberId));
      applyBoard(boardPayload);
      setRefreshErrorModel(null);
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      // soft/background failure：保留 ready content，不升級為 full-page phase。
      if (soft) {
        setRefreshErrorModel(mapApiErrorToUiModel(cause, { t }));
        return;
      }
      const kind = classifyLogisticsError(cause);
      if (kind === 'not-found' || classifyJourneyAccessError(cause) === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || classifyJourneyAccessError(cause) === 'forbidden') setPhase('forbidden');
      else {
        setErrorModel(mapApiErrorToUiModel(cause, { t }));
        setPhase('error');
      }
    }
  }, [applyBoard, journeyId, t, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, load]);

  const canWrite = WRITABLE_LIFECYCLES.has(journey?.lifecycleStatus)
    && WRITE_ROLES.has(journey?.requesterRole);
  const canRemove = WRITABLE_LIFECYCLES.has(journey?.lifecycleStatus)
    && REMOVE_ROLES.has(journey?.requesterRole);

  async function runMutation(work, { itemId = null, clearDraft = false, refreshOnValidation = false } = {}) {
    if (mutating.current) return;
    mutating.current = true;
    setBusy(true);
    setBusyItemId(itemId);
    setFormError('');
    try {
      await work();
      if (clearDraft) {
        setDraftTitle('');
        setDraftAssignee('');
      }
      setConfirmDelete(null);
      await load({ soft: true });
    } catch (cause) {
      const kind = classifyLogisticsError(cause);
      const assigneeMessage = checklistAssigneeErrorMessage(cause);
      if (kind === 'validation') {
        const message = assigneeMessage || WORKSPACE_PRODUCT_CHROME.formCheckFallback;
        if (refreshOnValidation) await load({ soft: true });
        setFormError(message);
      } else if (kind === 'forbidden') setFormError(WORKSPACE_PRODUCT_CHROME.permissionDeniedChange);
      else if (kind === 'not-found') setFormError(WORKSPACE_PRODUCT_CHROME.checklistItemNotFound);
      else {
        setFormError(
          resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed')
            || WORKSPACE_PRODUCT_CHROME.checklistChangeFailed,
        );
      }
    } finally {
      mutating.current = false;
      setBusy(false);
      setBusyItemId(null);
    }
  }

  async function submitAdd(event) {
    event.preventDefault();
    const draft = {
      title: draftTitle,
      assignedMemberId: draftAssignee || null,
    };
    const validation = validateChecklistCreateDraft(draft);
    if (validation) {
      setFormError(validation);
      return;
    }
    const body = buildChecklistCreatePayload(draft);
    await runMutation(async () => {
      await journeyChecklistApi.createItem(journey.journeyId, body);
    }, { clearDraft: true, refreshOnValidation: true });
  }

  async function toggleComplete(item) {
    if (!canWrite || busy) return;
    await runMutation(async () => {
      if (item.completed) {
        await journeyChecklistApi.reopenItem(journey.journeyId, item.itemId);
      } else {
        await journeyChecklistApi.completeItem(journey.journeyId, item.itemId);
      }
    }, { itemId: item.itemId });
  }

  async function reassignItem(item, rawValue) {
    if (!canWrite || busy) return;
    if (String(rawValue).startsWith('historical:')) return;
    const current = item.assignedMemberId == null || item.assignedMemberId === ''
      ? null
      : String(item.assignedMemberId);
    const next = rawValue === '' ? null : String(rawValue);
    if (current === next) return;
    await runMutation(async () => {
      await journeyChecklistApi.reassignItem(
        journey.journeyId,
        item.itemId,
        buildChecklistReassignPayload(next).assignedMemberId,
      );
    }, { itemId: item.itemId, refreshOnValidation: true });
  }

  async function confirmRemove(item) {
    await runMutation(async () => {
      await journeyChecklistApi.removeItem(journey.journeyId, item.itemId, { confirm: true });
    }, { itemId: item.itemId });
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state">
        <LoadingState label={WORKSPACE_PRODUCT_CHROME.checklistLoading} data-testid="checklist-loading" />
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <EmptyState
          title={WORKSPACE_PRODUCT_CHROME.notFoundTitle}
          description={WORKSPACE_PRODUCT_CHROME.notFoundBody}
          action={<Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>}
        />
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <EmptyState
          title={WORKSPACE_PRODUCT_CHROME.forbiddenTitle}
          description={WORKSPACE_PRODUCT_CHROME.forbiddenBody}
          action={<Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>}
        />
      </main>
    );
  }
  if (phase === 'error') {
    const errorCopy = errorModel ? resolveUiErrorCopy(errorModel, t) : null;
    return (
      <main className="journey-workspace-state" data-testid="checklist-error">
        <ErrorState
          title={errorCopy?.title || t('travel.common.apiError.unknown.title')}
          description={errorCopy?.description || t('travel.common.apiError.unknown.description')}
          retryable={Boolean(errorCopy?.retryable)}
          retryLabel={t('travel.common.retry')}
          onRetry={() => load()}
        />
      </main>
    );
  }

  const progress = deriveChecklistProgress(board.items);
  const progressLabel = progress.total === 0
    ? WORKSPACE_PRODUCT_CHROME.checklistProgressEmpty
    : fillWorkspaceProductChrome(WORKSPACE_PRODUCT_CHROME.checklistProgress, {
      completed: progress.completed,
      total: progress.total,
    });
  const visibleItems = filterChecklistItems(board.items, filter);
  const refreshErrorCopy = refreshErrorModel ? resolveUiErrorCopy(refreshErrorModel, t) : null;

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{WORKSPACE_PRODUCT_CHROME.eyebrow}</p>
          <h1>{journey.title}</h1>
          <dl className="journey-workspace__facts">
            {journey.destination ? (
              <div>
                <dt>{WORKSPACE_PRODUCT_CHROME.destination}</dt>
                <dd>{journey.destination}</dd>
              </div>
            ) : null}
            {journey.dateRange ? (
              <div>
                <dt>{WORKSPACE_PRODUCT_CHROME.dates}</dt>
                <dd>{journey.dateRange}</dd>
              </div>
            ) : null}
            <div>
              <dt>{WORKSPACE_PRODUCT_CHROME.status}</dt>
              <dd>{journey.lifecycleLabel}</dd>
            </div>
            {journey.requesterRoleLabel ? (
              <div>
                <dt>{WORKSPACE_PRODUCT_CHROME.yourRole}</dt>
                <dd>{journey.requesterRoleLabel}</dd>
              </div>
            ) : null}
          </dl>
        </div>
        <Link className="journey-workspace__back" to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />

      <section className="journey-workspace__panel" aria-labelledby="checklist-heading">
        <div className="journey-workspace__panel-head">
          <div>
            <h2 id="checklist-heading">{WORKSPACE_PRODUCT_CHROME.checklist}</h2>
            <p className="journey-checklist__progress" aria-live="polite">{progressLabel}</p>
          </div>
          <div className="journey-logistics__toolbar">
            <Button type="button" variant="secondary" onClick={() => load()} disabled={busy}>
              {WORKSPACE_PRODUCT_CHROME.refresh}
            </Button>
          </div>
        </div>

        {refreshErrorCopy ? (
          <Alert
            variant="error"
            title={refreshErrorCopy.title}
            data-testid="checklist-refresh-error"
          >
            <p>{refreshErrorCopy.description}</p>
          </Alert>
        ) : null}

        {formError ? <Alert variant="error">{formError}</Alert> : null}

        {canWrite ? (
          <form className="journey-checklist__add" onSubmit={submitAdd} noValidate>
            <TextField
              id={titleId}
              label={WORKSPACE_PRODUCT_CHROME.checklistNewItem}
              value={draftTitle}
              maxLength={200}
              disabled={busy}
              placeholder={WORKSPACE_PRODUCT_CHROME.checklistNewItemPlaceholder}
              onChange={(e) => setDraftTitle(e.target.value)}
            />
            <label htmlFor={assigneeId}>
              指派給
              <select
                id={assigneeId}
                data-testid="checklist-assignee-select"
                value={draftAssignee}
                disabled={busy}
                onChange={(e) => setDraftAssignee(e.target.value)}
              >
                <option value="">未指派</option>
                {eligibleAssignees.map((member) => {
                  const id = String(member.memberId);
                  return (
                    <option key={id} value={id}>
                      {memberDisplayName(id, memberSummaries)}
                      （{member.roleLabel || member.role}）
                    </option>
                  );
                })}
              </select>
            </label>
            <Button type="submit" loading={busy && !busyItemId}>
              {busy && !busyItemId
                ? WORKSPACE_PRODUCT_CHROME.checklistAdding
                : WORKSPACE_PRODUCT_CHROME.checklistAddItem}
            </Button>
          </form>
        ) : null}

        <div
          className="journey-checklist__filters"
          role="group"
          aria-label={WORKSPACE_PRODUCT_CHROME.checklistFilterAria}
        >
          {[
            { id: 'all', label: WORKSPACE_PRODUCT_CHROME.checklistFilterAll },
            { id: 'open', label: WORKSPACE_PRODUCT_CHROME.checklistFilterOpen },
            { id: 'completed', label: WORKSPACE_PRODUCT_CHROME.checklistFilterCompleted },
          ].map((option) => (
            <button
              key={option.id}
              type="button"
              className={filter === option.id ? 'is-active' : undefined}
              aria-pressed={filter === option.id}
              disabled={busy}
              onClick={() => setFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {board.items.length === 0 ? (
          <EmptyState
            className="journey-checklist__empty"
            title="還沒有待辦事項"
            description="新增第一項，讓同行者知道要準備什麼。"
            action={
              canWrite ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => document.getElementById(titleId)?.focus()}
                >
                  新增第一項
                </Button>
              ) : null
            }
          />
        ) : visibleItems.length === 0 ? (
          <EmptyState
            title={WORKSPACE_PRODUCT_CHROME.checklistFilterEmptyTitle}
            description={WORKSPACE_PRODUCT_CHROME.checklistFilterEmptyDescription}
          />
        ) : (
          <ul className="journey-checklist__list">
            {visibleItems.map((item) => {
              const itemBusy = busy && busyItemId === item.itemId;
              const assignee = resolveChecklistAssigneePresentation({
                assignedMemberId: item.assignedMemberId,
                members: journey.members,
                summariesById: memberSummaries,
              });
              return (
                <li key={item.itemId} className={item.completed ? 'is-completed' : undefined}>
                  <label className="journey-checklist__item">
                    <input
                      type="checkbox"
                      checked={item.completed}
                      disabled={!canWrite || busy}
                      aria-label={fillWorkspaceProductChrome(
                        item.completed
                          ? WORKSPACE_PRODUCT_CHROME.checklistReopenAria
                          : WORKSPACE_PRODUCT_CHROME.checklistCompleteAria,
                        { title: item.title },
                      )}
                      onChange={() => toggleComplete(item)}
                    />
                    <span className="journey-checklist__item-body">
                      <strong>{item.title}</strong>
                      <span className="journey-checklist__assignee" data-testid={`checklist-assignee-${item.itemId}`}>
                        {assignee.kind === 'unassigned'
                          ? '指派：未指派'
                          : `指派：${assignee.label}`}
                      </span>
                      <span className="journey-checklist__status">
                        {item.completed
                          ? WORKSPACE_PRODUCT_CHROME.checklistStatusCompleted
                          : WORKSPACE_PRODUCT_CHROME.checklistStatusOpen}
                        {itemBusy ? ` · ${WORKSPACE_PRODUCT_CHROME.checklistItemSaving}` : ''}
                      </span>
                    </span>
                  </label>
                  {canWrite ? (
                    <label className="journey-checklist__reassign">
                      改指派
                      <select
                        aria-label={`改指派 ${item.title}`}
                        data-testid={`checklist-reassign-${item.itemId}`}
                        value={checklistReassignSelectValue(item.assignedMemberId, eligibleAssignees)}
                        disabled={busy}
                        onChange={(event) => reassignItem(item, event.target.value)}
                      >
                        <option value="">未指派</option>
                        {item.assignedMemberId != null
                          && item.assignedMemberId !== ''
                          && !eligibleAssignees.some((member) => String(member.memberId) === String(item.assignedMemberId)) ? (
                            <option value={`historical:${item.assignedMemberId}`} disabled>
                              {`目前：${assignee.label}`}
                            </option>
                          ) : null}
                        {eligibleAssignees.map((member) => {
                          const id = String(member.memberId);
                          return (
                            <option key={id} value={id}>
                              {memberDisplayName(id, memberSummaries)}
                              （{member.roleLabel || member.role}）
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  ) : null}
                  {canRemove ? (
                    <button
                      type="button"
                      className="journey-checklist__delete"
                      disabled={busy}
                      onClick={() => setConfirmDelete(item)}
                    >
                      {WORKSPACE_PRODUCT_CHROME.delete}
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {confirmDelete ? (
        <div className="journey-logistics__dialog" role="dialog" aria-modal="true" aria-labelledby="delete-checklist-title">
          <div className="journey-logistics__dialog-card">
            <h3 id="delete-checklist-title">{WORKSPACE_PRODUCT_CHROME.checklistDeleteTitle}</h3>
            <p>
              {fillWorkspaceProductChrome(WORKSPACE_PRODUCT_CHROME.checklistDeleteBody, {
                name: confirmDelete.title,
              })}
            </p>
            <div className="journey-workspace__edit-actions">
              <button
                type="button"
                className="journey-checklist__delete-confirm"
                disabled={busy}
                onClick={() => confirmRemove(confirmDelete)}
              >
                {busy ? WORKSPACE_PRODUCT_CHROME.deleting : WORKSPACE_PRODUCT_CHROME.delete}
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirmDelete(null)}>
                {WORKSPACE_PRODUCT_CHROME.cancel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
